import { MapPin, Calendar, Users, TreePine, LogIn, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useEffect } from 'react';
import { trackPageView } from '../utils/analytics';
import ThankYouBanner from './ThankYouBanner';
import AboutHistory from './AboutHistory';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';

const RetreatLanding = () => {
  // Track page view on mount
  useEffect(() => {
    trackPageView('Home');
  }, []);

  return (
    <div className="min-h-screen bg-cream-50">
      <SiteHeader active="home" />

      <ThankYouBanner />

      {/* Hero Section — video background + heritage overlay */}
      <section className="relative overflow-hidden bg-[#1a0d10] text-white py-28 md:py-40 px-4 md:px-6 min-h-[85vh] flex items-center">
        {/* Background video */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/hero-poster.jpg"
          aria-hidden="true"
          data-testid="hero-video"
        >
          <source src="/hero-720.mp4" type="video/mp4" />
        </video>

        {/* Warm burgundy tint + vignette so the copy stays readable */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0d10]/70 via-[#1a0d10]/55 to-[#1a0d10]/85" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(26,13,16,0.65)_90%)]" />
        <div className="absolute inset-0 grain opacity-60" />

        <div className="max-w-5xl mx-auto text-center relative z-10 w-full">
          <p className="eyebrow text-white/60 mb-6 animate-fade-in tracking-[0.3em]">— 2027 · You&apos;re Invited Back —</p>
          <h2 className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-[8rem] font-medium leading-[0.95] tracking-tighter mb-8 animate-slide-up drop-shadow-[0_4px_28px_rgba(0,0,0,0.45)]">
            <span className="block">ROJO</span>
            <span className="block bg-gradient-to-br from-[#c98a4b] via-[#d9a15b] to-[#f0d3a0] bg-clip-text text-transparent italic">Recovery</span>
            <span className="block text-white/95">Retreat</span>
          </h2>
          <p className="text-base md:text-xl text-white/80 font-light mb-12 max-w-2xl mx-auto leading-relaxed animate-slide-up-1 drop-shadow-lg">
            An inward and outward journey for men in recovery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 items-center justify-center text-sm text-white/85 animate-slide-up-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#d9a15b]" />
              <span className="font-medium tracking-wide" data-testid="hero-dates">September 2027 · Dates coming soon</span>
            </div>
            <div className="hidden sm:block w-1 h-1 rounded-full bg-white/40" />
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#d9a15b]" />
              <span className="font-medium tracking-wide">Herzl Camp · Webster, WI</span>
            </div>
          </div>
          <div className="mt-12 animate-slide-up-3">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center flex-wrap">
              <Link to="/login" data-testid="hero-login-btn">
                <Button
                  size="lg"
                  className="bg-cream-50 text-wine-800 hover:bg-white font-semibold text-base px-8 sm:px-10 py-6 rounded-full btn-lift group shadow-xl"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Access My Portal
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* What to Expect Section */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="eyebrow text-gray-400 mb-4">What Awaits</p>
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-gray-900">
              Experience the journey
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-gray-100 rounded-2xl overflow-hidden border border-gray-100">
            <div className="bg-white p-8 md:p-10 hover:bg-gray-50/50 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-wine to-burgundy flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-display text-2xl font-semibold mb-3 text-gray-900 tracking-tight">Brotherhood</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Connect with men who understand the journey of recovery in a supportive, judgment-free environment.
              </p>
            </div>
            <div className="bg-white p-8 md:p-10 hover:bg-gray-50/50 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-wine to-burgundy flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <TreePine className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-display text-2xl font-semibold mb-3 text-gray-900 tracking-tight">Nature immersion</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Disconnect from daily distractions and reconnect with yourself in the peaceful Wisconsin wilderness.
              </p>
            </div>
            <div className="bg-white p-8 md:p-10 hover:bg-gray-50/50 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-wine to-burgundy flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-display text-2xl font-semibold mb-3 text-gray-900 tracking-tight">A weekend that lands</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Three days of personal growth, reflection, and activities designed to strengthen your recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AboutHistory />

      {/* Attendee Portal CTA */}
      <section className="relative overflow-hidden py-20 md:py-28 px-4 md:px-6 bg-[#1a0d10] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,29,51,0.12),transparent_55%)]" />
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="eyebrow text-white/40 mb-4">Already Registered</p>
          <h3 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter mb-4">
            Your retreat awaits
          </h3>
          <p className="text-white/60 mb-10 max-w-lg mx-auto text-sm md:text-base leading-relaxed">
            Sign in to see your personalized ticket, room assignment, packing list, payment status, and more.
          </p>
          <Link to="/login" data-testid="section-login-btn">
            <Button
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-50 font-semibold text-base px-10 py-6 rounded-full btn-lift group"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In to My Portal
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default RetreatLanding;